import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import debounce from 'lodash/debounce'

import Autocomplete from '@mui/material/Autocomplete'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'

import { useLazyGetCountryQuery } from 'api/dictionary'
import { ADORNMENT_LOCATION } from 'config/constants'

import { getCountries } from '../invoice/components/invoiceDetails/components/products/components/row_code/helpers'

type CountryOption = {
  code: string
  label: string
  id: string
}

type Props = {
  countryCode: string
  hsCode: string
  countryError?: boolean
  codeError?: boolean
  onCountryChange: (code: string) => void
  onCodeChange: (code: string) => void
}

const DocumentImportRowCode = ({
  countryCode,
  hsCode,
  countryError,
  codeError,
  onCountryChange,
  onCodeChange,
}: Props) => {
  const { t } = useTranslation()
  const [ userHasStartedTyping, setUserHasStartedTyping ] = useState(false)
  const [ countriesRequest, { data, isLoading: isLoadingCountry, isSuccess: isSuccessCountries } ] = useLazyGetCountryQuery()
  const debouncedRequest = useRef(debounce(countriesRequest, 400)).current

  useEffect(() => {
    if (countryCode) {
      countriesRequest({ search: countryCode })
    }
  }, [ countryCode, countriesRequest ])

  const options = useMemo(() => getCountries(data), [ data ])

  const selectedCountry = useMemo((): CountryOption | null => {
    const fromOptions = options.find((option) => option.code === countryCode)
    if (fromOptions) {
      return fromOptions
    }
    if (!countryCode) {
      return null
    }
    return { id: countryCode, code: countryCode, label: countryCode }
  }, [ countryCode, options ])

  return (
    <div className='add-product-code document-import__row-code'>
      <Autocomplete
        className='add-product__country_code'
        options={options}
        value={selectedCountry}
        loading={isLoadingCountry}
        noOptionsText={t('calc__text__main__noOptionsFound')}
        onChange={(_event, value: CountryOption | null) => {
          if (!value) {
            return
          }
          onCountryChange(value.code)
        }}
        filterOptions={(items) => items}
        PaperComponent={({ children }) => (
          <Paper>
            {userHasStartedTyping && isSuccessCountries && children}
          </Paper>
        )}
        isOptionEqualToValue={(option, value) => option.code === value.code}
        loadingText={t('common__message__loadingText')}
        renderInput={(params) => (
          <TextField
            {...params}
            onChange={({ target: { value: query } }) => {
              if (query.length >= 2) {
                debouncedRequest({ search: query })
                setUserHasStartedTyping(true)
              }
            }}
            error={countryError}
            variant='outlined'
            label={t('calc__text__packaging__countryOfOrigin')}
            InputProps={{
              ...params.InputProps,
              startAdornment: ADORNMENT_LOCATION,
            }}
            InputLabelProps={{ shrink: true }}
          />
        )}
      />
      <div className='add-product__code'>
        <TextField
          error={codeError}
          variant='outlined'
          label={t('common__text__codeOfHS')}
          value={hsCode}
          onChange={({ target: { value } }) => {
            const digits = value.replace(/\D/g, '').slice(0, 10)
            onCodeChange(digits)
          }}
          inputProps={{ maxLength: 10 }}
          InputLabelProps={{ shrink: true }}
        />
      </div>
    </div>
  )
}

export default DocumentImportRowCode
